import { rest } from 'msw'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { Application, CustomerApplicationDetails } from 'types/customers'
import { API_PAGINATION_LIMIT } from 'utils/pagination'
import mockCustomers from '../../../__mocks__/Customers/customers.json'

const API_URL = process.env.API_URL

export const getCustomersHandler = rest.get(
  `${API_URL}/api/platform/v1/customers`,
  async (req, res, ctx) => {
    const page = Number.parseInt(req.url.searchParams.get('page') || '0', MIN_PAGINATION_SIZE)
    const size = Number.parseInt(req.url.searchParams.get('size') || '20', MIN_PAGINATION_SIZE)

    const totalElements = mockCustomers.length
    const totalPages = Math.ceil(totalElements / size)
    const startIndex = page * size
    const endIndex = startIndex + size
    const paginatedContent = mockCustomers.slice(startIndex, endIndex)

    return res(
      ctx.json({
        content: paginatedContent,
        size,
        totalElements,
        number: page,
        numberOfElements: paginatedContent.length,
        totalPages,
      })
    )
  }
)

export const getCustomerReferenceHandler = rest.get(
  `${API_URL}/api/platform/v1/customers/:customerReference`,
  async (req, res, ctx) => {
    const { customerReference } = req.params

    const customer = mockCustomers.find(c => c.reference === customerReference)
    if (customer) {
      return res(ctx.json(customer))
    }

    return res(
      ctx.status(404),
      ctx.json({
        message: 'Customer not found',
        status: 404,
      })
    )
  }
)

export const mockCustomerApplicationDetails: CustomerApplicationDetails = {
  id: 'app-001',
  status: 'VERIFIED',
  onboardingType: 'EMBEDDED',
  customerType: 'COMPANY',
  onboardingApplication: { documentsIntakeEmail: 'docs@example.com' },
  users: [
    {
      id: 'user-001',
      email: 'user@example.com',
      link: {
        link: 'https://onboarding.example.com/start',
        linkType: 'ONBOARDING',
        linkExpiresAt: '2025-01-01',
      },
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
    },
  ],
  company: {
    name: 'Acme Corp',
    entityType: 'PRIVATE_LIMITED',
    taxIdentification: { number: 'TAX-123', taxResidenceCountryCode: 'GB' },
    registrationNumber: 'REG-456',
    address: {
      addressLine1: '123 Main St',
      city: 'London',
      postalCode: 'EC1A 1BB',
      countryCode: 'GB',
    },
    isOperationalAddressDifferent: false,
    incorporationDate: '2010-01-01',
    businessOperationsStartDate: '2010-06-01',
    businessProfile: {
      naicsCode: '5211',
      monthlyExpectedVolumes: 'MEDIUM_VOLUME',
      isRegulated: false,
      sourceOfFunds: 'BUSINESS_INCOME',
      intendedUseOfAccount: 'PAYMENTS',
      website: '',
    },
  },
}

export const getCustomerApplicationHandler = rest.get(
  `${API_URL}/onboarding/v1/applications/:onboardingId`,
  async (_req, res, ctx) => {
    return res(ctx.json(mockCustomerApplicationDetails))
  }
)

export const getCustomerV2Handler = rest.get(
  `${API_URL}/platform/v2/customers/:onboardingId`,
  async (_req, res, ctx) => {
    return res(ctx.json(mockCustomerApplicationDetails))
  }
)

export const mockApplicationStatusResponse = {
  infoRequired: {
    data: [
      { code: 'CODE_1', name: 'Item 1', category: 'GET_STARTED' },
      { code: 'CODE_2', name: 'Item 2', category: 'COMPANY_INFORMATION' },
      { code: 'CODE_3', name: 'Item 3', category: 'COMPANY_INFORMATION' },
    ],
    missingRoles: ['ACCOUNT_REPRESENTATIVE', 'DIRECTOR'],
    blockers: [{ code: 'BLOCK_1', message: 'Missing document' }],
  },
}

export const getApplicationStatusHandler = rest.get(
  `${API_URL}/onboarding/v1/applications/:onboardingId/status`,
  async (_req, res, ctx) => {
    return res(ctx.json(mockApplicationStatusResponse))
  }
)

export const getApplicationStatusEmptyHandler = rest.get(
  `${API_URL}/onboarding/v1/applications/:onboardingId/status`,
  async (_req, res, ctx) => {
    return res(ctx.json({ infoRequired: { data: [], missingRoles: [], blockers: [] } }))
  }
)

export const getApplicationStatusErrorHandler = rest.get(
  `${API_URL}/onboarding/v1/applications/:onboardingId/status`,
  async (_req, res, ctx) => {
    return res(ctx.status(500))
  }
)

export const mockApplications: Application[] = [
  {
    id: 'app-001',
    reference: 'EXT-001',
    status: 'VERIFIED',
    model: 'EMBEDDED',
    type: 'COMPANY',
    name: 'Acme Payments LLC',
    createdAt: '2024-01-01T00:00:00Z',
    authenticatedLink: {
      link: 'https://onboarding.example.com/start',
      expiresAt: '2025-01-01T00:00:00Z',
    },
  },
  {
    id: 'app-002',
    reference: 'EXT-002',
    status: 'PENDING',
    model: 'EMBEDDED',
    type: 'INDIVIDUAL',
    name: 'John Doe',
    createdAt: '2024-01-01T00:00:00Z',
    authenticatedLink: {
      link: 'https://onboarding.example.com/start',
      expiresAt: '2025-01-01T00:00:00Z',
    },
  },
]

export const mockApplicationsItems: Application[] = [
  {
    id: 'app-001',
    reference: 'EXT-001',
    status: 'VERIFIED',
    model: 'EMBEDDED',
    type: 'COMPANY',
    name: 'Acme Payments LLC',
    createdAt: '2024-01-01T00:00:00Z',
    authenticatedLink: {
      link: 'https://onboarding.example.com/start',
      expiresAt: '2025-01-01T00:00:00Z',
    },
  },
  {
    id: 'app-002',
    reference: 'EXT-002',
    status: 'PENDING',
    model: 'EMBEDDED',
    type: 'INDIVIDUAL',
    name: 'John Doe',
    createdAt: '2024-01-01T00:00:00Z',
    authenticatedLink: {
      link: 'https://onboarding.example.com/start',
      expiresAt: '2025-01-01T00:00:00Z',
    },
  },
  {
    id: 'app-003',
    reference: 'EXT-003',
    status: 'INFO_REQUIRED',
    model: 'EMBEDDED',
    type: 'COMPANY',
    name: 'Globex Corp',
    createdAt: '2024-01-01T00:00:00Z',
    authenticatedLink: {
      link: 'https://onboarding.example.com/start',
      expiresAt: '2025-01-01T00:00:00Z',
    },
  },
  {
    id: 'app-004',
    reference: 'EXT-004',
    status: 'REJECTED',
    model: 'EMBEDDED',
    type: 'INDIVIDUAL',
    name: 'Jane Smith',
    createdAt: '2024-01-01T00:00:00Z',
    authenticatedLink: {
      link: 'https://onboarding.example.com/start',
      expiresAt: '2025-01-01T00:00:00Z',
    },
  },
  {
    id: 'app-005',
    reference: 'EXT-005',
    status: 'TERMINATED',
    model: 'EMBEDDED',
    type: 'COMPANY',
    name: 'Defunct Ltd',
    createdAt: '2024-01-01T00:00:00Z',
    authenticatedLink: {
      link: 'https://onboarding.example.com/start',
      expiresAt: '2025-01-01T00:00:00Z',
    },
  },
]

export const getApplicationsEmptyHandler = rest.get(
  `${API_URL}/platform/v2/customers`,
  async (_req, res, ctx) => {
    return res(
      ctx.json({
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: API_PAGINATION_LIMIT,
        number: 0,
        numberOfElements: 0,
      })
    )
  }
)

export const getApplicationsErrorHandler = rest.get(
  `${API_URL}/platform/v2/customers`,
  async (_req, res, ctx) => {
    return res(ctx.status(500))
  }
)

export const getApplicationsHandler = rest.get(
  `${API_URL}/platform/v2/customers`,
  async (req, res, ctx) => {
    const page = Number.parseInt(req.url.searchParams.get('pageNumber') || '0', MIN_PAGINATION_SIZE)
    const size = Number.parseInt(
      req.url.searchParams.get('pageSize') || String(API_PAGINATION_LIMIT),
      MIN_PAGINATION_SIZE
    )
    const search = req.url.searchParams.get('name')?.toLowerCase()
    const statuses = req.url.searchParams.get('statuses')?.split(',')
    const types = req.url.searchParams.get('types')?.split(',')

    let filtered = mockApplicationsItems
    if (search) {
      filtered = filtered.filter(
        app =>
          app.name.toLowerCase().includes(search) || app.reference.toLowerCase().includes(search)
      )
    }
    if (statuses?.length) {
      filtered = filtered.filter(app => statuses.includes(app.status))
    }
    if (types?.length) {
      filtered = filtered.filter(app => types.includes(app.type))
    }

    const totalElements = filtered.length
    const totalPages = Math.ceil(totalElements / size)
    const content = filtered.slice(page * size, page * size + size)
    return res(
      ctx.json({
        content,
        totalElements,
        totalPages,
        size,
        number: page,
        numberOfElements: content.length,
      })
    )
  }
)

export const getEmbeddedCustomerWithVerifiedAssociatesHandler = rest.get(
  `${API_URL}/api/platform/v1/customers/:customerReference`,
  async (_req, res, ctx) => {
    const customer = mockCustomers.find(c => c.reference === 'CUST009')
    return res(ctx.json(customer))
  }
)

export const getEmbeddedCustomerWithPendingAssociatesHandler = rest.get(
  `${API_URL}/api/platform/v1/customers/:customerReference`,
  async (_req, res, ctx) => {
    const customer = mockCustomers.find(c => c.reference === 'CUST010')
    return res(ctx.json(customer))
  }
)

export const getInfoRequiredEmptyHandler = rest.get(
  `${API_URL}/platform/v1/customers/:customerReference/info-required`,
  async (_req, res, ctx) => {
    return res(ctx.json({ status: 'VERIFIED', infoRequired: {} }))
  }
)

export const getInfoRequiredWithAssociatesHandler = rest.get(
  `${API_URL}/platform/v1/customers/:customerReference/info-required`,
  async (_req, res, ctx) => {
    return res(
      ctx.json({
        status: 'INFO_REQUIRED',
        infoRequired: {
          associates: [
            {
              customerPersonReference: 'person-ref-bob',
              associateName: 'Bob Jones',
              roles: ['BUSINESS_OWNER', 'DIRECTOR', 'ACCOUNT_REPRESENTATIVE'],
              documents: [
                { docSetType: 'ID_CARD', types: ['ID_CARD', 'PASSPORT'] },
                { docSetType: 'SELFIE', types: ['SELFIE'] },
              ],
              questionnaires: [],
            },
          ],
        },
      })
    )
  }
)

export default [
  getCustomersHandler,
  getCustomerReferenceHandler,
  getApplicationsHandler,
  getCustomerApplicationHandler,
  getCustomerV2Handler,
  getApplicationStatusHandler,
]
